import cloneDeep from 'lodash.clonedeep';
import isEqual from 'lodash.isequal';
import { mapAddress } from '@general/js/utils/JoinNow';
import { formatFormDate, removeWhitespaceFromString } from '@general/js/utils';
import { CoverTypeValue, NeedsAnalysisEligibility, LifetimeHealthCoverSituation } from '@general/js/enums';
import { showConcessionCardSection as showConcessionCard, showConcessionCardDetailsForm } from '@/utils';
import { ContactNumberType, OperationType } from '@/enums';
import { isEmpty } from 'lodash';
import { mapLhcPayload } from '../GetPremiumPrice/PersonUtils';
import { mapVeteranCardPayload, mapConcessionCardPayload, isFundAutomaticTransfer } from '../ChangeCoverUtils';

// eslint-disable-next-line import/prefer-default-export
export const mapPartnerObj = (initialPartner, currentPartner, changeCoverData, currentPolicyHolder, extraData, existingPartnerDetails) => {
  // if a new partner is added
  let op = OperationType.NONE;

  const initialPostal = mapAddress(initialPartner.postalAddress, initialPartner.differentPostalAddress, initialPartner.POBox);
  let currentPostal = mapAddress(currentPartner.postalAddress, currentPartner.differentPostalAddress, currentPartner.POBox);

  // if had postal address and still has it, but details have changed, operation is REPLACE
  // if did not have postal address before and does have it now, operation is ADD
  // if did not have postal address before and does not have it now, operation is NONE
  // if had postal and still has it, but details did not change, operation is NONE
  let postalOp = OperationType.NONE;
  if (initialPostal?.addressLine1 && !isEqual(initialPostal, currentPostal)) {
    postalOp = OperationType.REPLACE;
  } else if (!initialPostal?.addressLine1) {
    postalOp = OperationType.ADD;
  }

  const noLongerEligibleForVeteran =
    initialPartner.veteranCard.haveVeteranCard &&
    (changeCoverData.step1.eligibility === NeedsAnalysisEligibility.WORK_CONNECTION || changeCoverData.step1.eligibility === NeedsAnalysisEligibility.RELATED);

  const veteranCardObj = {
    hasVeteranCard: noLongerEligibleForVeteran ? false : currentPartner.veteranCard.haveVeteranCard,
    veteranCardList: mapVeteranCardPayload(initialPartner.veteranCard, currentPartner.veteranCard, noLongerEligibleForVeteran),
  };

  // if had initial concession cards, and no longer eligible for concession, remove all cards in list
  const addressState = changeCoverData.step1.state;
  const coverType = changeCoverData.step3.coverType;
  const extrasOnly = coverType === CoverTypeValue.EXTRAS_ONLY;
  const partnerDOB = changeCoverData.step2.partnerDOB;
  const noLongerEligibleForConcession = initialPartner.haveConcessionCard && !showConcessionCard(addressState, partnerDOB, extrasOnly);

  const concessionCardObj = {
    hasConcessionCard: noLongerEligibleForConcession ? false : currentPartner.haveConcessionCard,
    cardList: mapConcessionCardPayload(
      initialPartner.haveConcessionCard,
      initialPartner.selectedConcessionCardOptions,
      currentPartner.haveConcessionCard,
      currentPartner.selectedConcessionCardOptions,
      noLongerEligibleForConcession,
      showConcessionCardDetailsForm(addressState, partnerDOB, extrasOnly, currentPartner.haveConcessionCard),
    ),
  };

  // The person code should be sent if the old composition had partner, the new composition has a partner and they are
  // keeping the adult partner. The new composition is already checked by the calling function.
  const shouldSendPartnerPersonCode = extraData.oldCompositionHavePartner && !extraData.showNewPartnerSection;

  // Determine the op of the partner once every other item has been completed
  if (extraData.showNewPartnerSection) {
    // New partner section has been shown so new partner being added.
    op = OperationType.ADD;
  } else if (extraData.oldCompositionHavePartner) {
    // The partner section has not been shown so check if the policy previously had a partner
    // Check if there are any changes in the veteran card, concession card, or address that may trigger a replace of the partner
    const addressChanged = !isEqual(initialPartner.postalAddress, currentPartner.postalAddress);
    const veteranCardChanged = !isEqual(initialPartner.veteranCard, currentPartner.veteranCard);
    const concessionCardChanged =
      initialPartner.haveConcessionCard !== currentPartner.haveConcessionCard ||
      !isEqual(initialPartner.selectedConcessionCardOptions, currentPartner.selectedConcessionCardOptions);

    if (addressChanged || veteranCardChanged || concessionCardChanged || noLongerEligibleForConcession) {
      op = OperationType.REPLACE;
    }
  }
  if (currentPostal === null) {
    currentPostal = currentPolicyHolder.residentialAddress; // if no postal address, take the residential address
  }

  let partner = {
    op,
    personCode: shouldSendPartnerPersonCode ? currentPartner.personCode : '',
    postalAddress: {
      op: postalOp,
      ...currentPostal,
    },
    veteranCard: currentPartner.veteranCard.haveVeteranCard !== null ? veteranCardObj : null,
    concessionCard: currentPartner.haveConcessionCard !== null ? concessionCardObj : null,
  };

  // LHC and ABD are stored in the same partner object so need seperate logic for existing / new partner
  if (!extraData.showNewPartnerSection) {
    // Only send abd details if being added for the first time and no existing portalAgeBasedDiscount record
    const initialAbd = !isEmpty(initialPartner.abd) ? initialPartner.abd : null;
    const currentAbd = !isEmpty(currentPartner.abd) ? currentPartner.abd : null;
    const initialPortalAbd = !isEmpty(initialPartner.portalAgeBasedDiscount) ? initialPartner.portalAgeBasedDiscount : null;
    if (initialAbd === null && currentAbd !== null && currentAbd.cda && !initialPortalAbd) {
      partner.op = OperationType.REPLACE;
      partner.cda = currentAbd.cda;
    }

    // Send LHC if it doesn't already exist
    const initialLhc = !isEmpty(initialPartner.lhc) ? mapLhcPayload(initialPartner.lhc) : null;
    const currentLhc = !isEmpty(currentPartner.lhc) ? mapLhcPayload(currentPartner.lhc) : null;

    const existingLhcOverrule = existingPartnerDetails?.lhcOverrule !== null;

    if (!existingLhcOverrule) {
      if (initialLhc === null && currentLhc !== null && currentLhc.overruledReason?.toLowerCase() !== LifetimeHealthCoverSituation.NONE.toLowerCase()) {
        partner.op = OperationType.REPLACE;
        partner.lifetimeHealthCoverOverrule = currentLhc;
      }
    }
  }

  let newPartner;

  if (extraData.showNewPartnerSection) {
    const newPartnerObj = cloneDeep(currentPartner.newPartner);

    const mobileObj = newPartnerObj.preferredContactMethods.filter((contact) => {
      return contact.type.toLowerCase() === ContactNumberType.MOBILE;
    });

    // if no postal address is added for new partner, send residential address, otherwise, send the postal address with operation ADD
    const postalAddress =
    newPartnerObj.postalAddress && Object.keys(newPartnerObj.postalAddress).length > 0
      ? { ...mapAddress(newPartnerObj.postalAddress, newPartnerObj.differentPostalAddress, newPartnerObj.POBox), op: OperationType.ADD }
      : { op: OperationType.ADD, ...currentPolicyHolder.postalAddress };

    newPartner = {
      firstName: newPartnerObj.firstName,
      middleName: newPartnerObj.middleName,
      lastName: newPartnerObj.lastName,
      gender: newPartnerObj.gender,
      dateOfBirth: formatFormDate(newPartnerObj.DOB),
      mobile: mobileObj.length > 0 ? removeWhitespaceFromString(mobileObj[0].number) : '',
      email: newPartnerObj.email,
      isSameTransferFund: newPartnerObj.transferFromOthers,
      postalAddress,
      previousFunds: newPartnerObj.transferFromOthers
        ? currentPartner.newPartner.selectedInsuranceFund.map((fund) => {
            return {
              isAutomaticTransfer: isFundAutomaticTransfer(fund.name, extraData.fundNamesNewPerson),
              fundCode: fund.name.split('-')[0], // Split to only get the inbound transfer code and not include fund name
              memberNumber: fund.number,
            };
          })
        : [],
    };

    // Send new partner LHC object if captured
    const newPartnerLhc = !isEmpty(currentPartner.lhc) ? mapLhcPayload(currentPartner.lhc) : null;
    if (newPartnerLhc !== null && newPartnerLhc.overruledReason?.toLowerCase() !== LifetimeHealthCoverSituation.NONE.toLowerCase()) {
      newPartner.lifetimeHealthCoverOverrule = newPartnerLhc;
    }

    // Send new partner ABD object if captured
    const newPartnerAbd = !isEmpty(currentPartner.abd) ? currentPartner.abd : null;
    if (newPartnerAbd && newPartnerAbd.cda) {
      newPartner.cda = newPartnerAbd.cda;
    }
  }

  if (newPartner) {
    partner = { ...partner, ...newPartner };
    if (currentPartner.newPartner && currentPartner.newPartner.chosenDuplicatePerson) {
      partner.personCode = currentPartner.newPartner.chosenDuplicatePerson.personCode;
    } else if (partner.personCode) {
      delete partner.personCode;
    }

    if (currentPartner.newPartner) {
      partner.ignoreDuplicatePerson = currentPartner.newPartner.ignoreDuplicatePerson;
    }
  }

  return partner;
};
